import React from 'react';

import { ProfileItemMeta, ProfileItemTitle } from '@/components/profile/ProfileText';
import { VStack } from '@/components/ui/vstack';

/**
 * A structured part of a profile bio item, keyed by its role in the type scale
 * rather than by any one section's vocabulary, so work history and education
 * both decompose into these parts and render the same on every surface.
 *
 * `title` and `meta` mirror the type-scale components `ProfileItemTitle` and
 * `ProfileItemMeta`; `paragraph` and `bullets` are the two layouts a supporting
 * text block can take.
 *
 * - `title`: the item's primary line, such as a job title or degree. Green.
 * - `meta`: a muted secondary line beneath the title, such as an organization.
 * - `paragraph`: a supporting text block laid out as plain lines.
 * - `bullets`: a supporting text block laid out as a bulleted list.
 */
export type ProfileItemPart = {
  type: 'title' | 'meta' | 'paragraph' | 'bullets';
  text: string;
};

/**
 * Renders a single structured profile item part through the shared type scale.
 * Shared by work history and education so a bio item reads identically on the
 * profile page and the inquiry sidebar. See `.claude/rules/profile.md`.
 *
 * @param item - The structured part to render.
 * @returns The styled node for that part.
 */
/**
 * Renders a multi-line supporting text block (a `paragraph` or `bullets` part)
 * as indented meta-styled lines. Bulleted lines are stripped of any existing
 * list marker and prefixed with a single bullet so the markup stays uniform.
 */
function renderTextBlock(text: string, bulleted: boolean): React.ReactNode {
  return (
    <VStack className="mb-1 pl-2">
      {text
        .split('\n')
        .filter((line) => line.trim() !== '')
        .map((line, index) => {
          const content = bulleted
            ? `• ${line
                .trim()
                .replace(/^[•\-*\d+.\s]+/, '')
                .trim()}`
            : line;

          return (
            <ProfileItemMeta key={index} className="mb-1 leading-tight">
              {content}
            </ProfileItemMeta>
          );
        })}
    </VStack>
  );
}

export function renderProfileItemPart({ item }: { item: ProfileItemPart }): React.ReactNode {
  if (item.type === 'title') {
    return <ProfileItemTitle className="mb-1 leading-tight">{item.text}</ProfileItemTitle>;
  }

  if (item.type === 'meta') {
    return <ProfileItemMeta className="mb-1 leading-tight">{item.text}</ProfileItemMeta>;
  }

  return renderTextBlock(item.text, item.type === 'bullets');
}
